import { useEffect, useState } from "react";
import { Trophy, Crown, Clock } from "lucide-react";

import { api } from "../lib/api";
import {
  SC_DEFAULT_CONFIG,
  SIZE_MAP,
  topListLabel,
  formatEndsAt,
  campaignActivitySummary,
} from "../lib/shareCardConfig";

const LOGO_URL =
  "https://www.ontour.scootive.pl/img/logo.svg";

// Subtle grain texture overlay — adds film-like noise on top of the smooth
// gradient. Generated server-side once via feTurbulence + colour-shift to
// keep it monochrome and gentle.
const GRAIN_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="320" viewBox="0 0 320 320">
    <filter id="n">
      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/>
      <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0"/>
    </filter>
    <rect width="100%" height="100%" filter="url(#n)"/>
  </svg>`
)}`;

const GRAIN_STYLE = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  backgroundImage: `url("${GRAIN_SVG}")`,
  backgroundSize: "320px 320px",
  mixBlendMode: "multiply",
  opacity: 0.08,
  zIndex: 1,
};

const FALLBACK = {
  campaign: {
    title: "SCOOTIVE ON TOUR — SIERPIEŃ 2026",
    description: "Wybierzcie miejsce, do którego pojedzie team Scootive w wakacje!",
    results_title: "Top miasta",
    ends_at: "2026-08-31T22:59:00.000Z",
  },
  total: 12_847,
  winner: { city: "Gdańsk", skatepark: "Skatepark Brzeźno", region: "Pomorskie", votes: 3214, pct: 25.0 },
  top: [
    { city: "Warszawa", skatepark: "Skatepark Warszawa-Mokotów", region: "Mazowieckie", votes: 2871, pct: 22.3 },
    { city: "Kraków", skatepark: "Skatepark Mistrzejowice", region: "Małopolskie", votes: 1936, pct: 15.1 },
    { city: "Wrocław", skatepark: "Skatepark Karłowice", region: "Dolnośląskie", votes: 1442, pct: 11.2 },
    { city: "Poznań", skatepark: "Skatepark Cegielnia", region: "Wielkopolskie", votes: 987, pct: 7.7 },
  ],
};

function winnerFontSize(name, baseSize) {
  const len = (name || "").length;
  if (len <= 8) return baseSize;
  if (len <= 12) return Math.round(baseSize * 0.85);
  if (len <= 16) return Math.round(baseSize * 0.7);
  return Math.round(baseSize * 0.58);
}

function EndsAtPill({ endsAt, accent }) {
  const label = formatEndsAt(endsAt);
  if (!label) return null;
  return (
    <div
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
      style={{ background: `${accent}14`, color: accent }}
    >
      <Clock style={{ width: 11, height: 11 }} />
      <span
        className="uppercase tracking-[0.2em]"
        style={{ fontSize: 9, fontWeight: 600 }}
      >
        {label}
      </span>
    </div>
  );
}

function TopList({ rows, accent, config, compact = false }) {
  const maxVotes = rows[0]?.votes || 1;
  return (
    <ol className={compact ? "space-y-1" : "space-y-2.5"}>
      {rows.map((r, i) => {
        const pct = Math.max(6, (r.votes / maxVotes) * 100);
        const hasSub =
          (config.showWinnerDescription && r.skatepark) ||
          (config.showWinnerRegion && r.region);
        return (
          <li
            key={`${r.city}-${i}`}
            style={
              r.isWinner
                ? {
                    background: `linear-gradient(90deg, ${accent}14 0%, ${accent}05 70%, transparent 100%)`,
                    borderRadius: 10,
                    padding: compact ? "4px 8px" : "6px 10px",
                    marginLeft: compact ? -8 : -10,
                    marginRight: compact ? -8 : -10,
                  }
                : undefined
            }
          >
            <div className="flex items-baseline justify-between gap-2">
              <div className="flex items-baseline gap-2 min-w-0 flex-1">
                <span
                  style={{
                    fontFamily: '"Bodoni Moda", serif',
                    fontStyle: "italic",
                    fontSize: compact ? 14 : 18,
                    color: r.isWinner ? accent : "rgba(0,0,0,0.28)",
                    width: compact ? 18 : 22,
                    flexShrink: 0,
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                {r.isWinner && (
                  <Trophy
                    style={{
                      width: compact ? 10 : 12,
                      height: compact ? 10 : 12,
                      color: accent,
                      flexShrink: 0,
                    }}
                  />
                )}
                <div className="flex flex-col min-w-0">
                  <span
                    className="uppercase tracking-wide truncate"
                    style={{
                      fontWeight: r.isWinner ? 700 : 600,
                      fontSize: compact ? 12 : 13,
                      color: r.isWinner ? "#000" : "rgba(0,0,0,0.78)",
                      lineHeight: 1.15,
                    }}
                  >
                    {r.city}
                  </span>
                  {hasSub && !compact && (
                    <span
                      className="truncate"
                      style={{
                        fontFamily: '"Bodoni Moda", serif',
                        fontStyle: "italic",
                        fontSize: 11,
                        color: "rgba(0,0,0,0.45)",
                        lineHeight: 1.25,
                        marginTop: 1,
                      }}
                    >
                      {config.showWinnerDescription && r.skatepark}
                      {config.showWinnerDescription && config.showWinnerRegion && r.skatepark && r.region && " · "}
                      {config.showWinnerRegion && r.region}
                    </span>
                  )}
                </div>
                {hasSub && compact && (
                  <span
                    className="truncate min-w-0"
                    style={{
                      fontFamily: '"Bodoni Moda", serif',
                      fontStyle: "italic",
                      fontSize: 10,
                      color: "rgba(0,0,0,0.4)",
                    }}
                  >
                    {config.showWinnerDescription && r.skatepark}
                    {config.showWinnerDescription && config.showWinnerRegion && r.skatepark && r.region && " · "}
                    {config.showWinnerRegion && r.region}
                  </span>
                )}
              </div>
              <span
                className="tabular-nums shrink-0"
                style={{
                  fontSize: compact ? 10 : 11,
                  color: "rgba(0,0,0,0.5)",
                }}
              >
                {r.votes.toLocaleString("pl-PL")} · {r.pct}%
              </span>
            </div>
            <div className={`h-[3px] w-full bg-black/5 overflow-hidden rounded-full ${compact ? "mt-0.5" : "mt-1"}`}>
              <div className="h-full" style={{ width: `${pct}%`, background: accent, borderRadius: 2 }} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ShareHeader({ accent }) {
  return (
    <div className="relative flex items-center justify-between flex-shrink-0 z-10">
      <img src={LOGO_URL} alt="Scootive" style={{ height: 24 }} />
      <span className="text-[10px] uppercase tracking-[0.35em]" style={{ color: accent }}>
        Wyniki kampanii
      </span>
    </div>
  );
}

function ShareFooter({ config, compact = false }) {
  return (
    <div className="relative flex-shrink-0 z-10">
      <div
        className={`flex items-center justify-between rounded-2xl ${compact ? "py-2 px-4" : "p-3.5"}`}
        style={{ background: "#0e1410" }}
      >
        <div className="min-w-0">
          {!compact && (
            <p
              className="text-[9px] uppercase tracking-[0.4em]"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              Zobacz całość
            </p>
          )}
          <p
            className="font-display uppercase text-white truncate"
            style={{ fontSize: compact ? 14 : 22, letterSpacing: compact ? 0.5 : 0 }}
          >
            {config.domain}
          </p>
        </div>
        {config.showHashtag && (
          <p
            className="text-[10px] uppercase tracking-[0.3em] text-right shrink-0 ml-3"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            #{config.hashtag}
            {!compact && (
              <>
                <br />
                <span className="text-white">{new Date().getFullYear()}</span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

function StoryCard({ data, config }) {
  const w = data.winner;
  const a = config.accent;
  const winnerBase = SIZE_MAP.story.winner[config.winnerSize] || 60;
  const campBase = SIZE_MAP.story.campaign[config.campaignTitleSize] || 14;
  const fullTop = [{ ...w, isWinner: true }, ...(data.top || [])].slice(0, 5);
  const topLabel = topListLabel(config, data.campaign, 5);
  const cityFont = winnerFontSize(w.city, winnerBase);
  // Crown matches winner font size (slightly smaller so it sits at cap-height)
  const crownSize = Math.round(cityFont * 0.78);
  const activity = campaignActivitySummary(data.campaign, data.total);

  return (
    <div
      className="story-card relative overflow-hidden flex flex-col"
      style={{
        width: 540,
        height: 960,
        borderRadius: 28,
        background: "linear-gradient(180deg, #ffffff 0%, #f1f7f1 55%, #d8ebd8 100%)",
        padding: "24px 26px",
      }}
    >
      <div style={GRAIN_STYLE} />
      <div
        className="absolute inset-x-0 top-3 text-center font-display uppercase pointer-events-none select-none"
        style={{ fontSize: 92, letterSpacing: -2, color: `${a}10` }}
      >
        ON TOUR
      </div>

      <ShareHeader accent={a} />

      {/* CONTENT — vertically centered between header & footer */}
      <div className="relative flex-1 min-h-0 flex flex-col justify-center gap-5 my-4 z-10">
        {/* Campaign title / description (centered) */}
        {(config.showCampaignTitle || config.showCampaignDescription) && (
          <div className="text-center">
            {config.showCampaignTitle && data.campaign?.title && (
              <p
                className="font-display uppercase leading-tight"
                style={{ fontSize: campBase * 1.3, letterSpacing: -0.5 }}
              >
                {data.campaign.title}
              </p>
            )}
            {config.showCampaignDescription && data.campaign?.description && (
              <p
                className="mx-auto"
                style={{
                  fontFamily: '"Bodoni Moda", serif',
                  fontStyle: "italic",
                  fontSize: Math.max(11, Math.round(campBase * 0.9)),
                  color: "rgba(0,0,0,0.55)",
                  marginTop: config.showCampaignTitle ? 6 : 0,
                  lineHeight: 1.3,
                  maxWidth: 440,
                }}
              >
                {data.campaign.description}
              </p>
            )}
          </div>
        )}

        {/* Winner — fully centered */}
        <div className="text-center">
          <p
            className="text-[10px] font-medium uppercase tracking-[0.4em]"
            style={{ color: "rgba(0,0,0,0.5)" }}
          >
            Zwycięzca
          </p>
          <div className="flex items-end justify-center gap-3 mt-1.5">
            <Crown style={{ width: crownSize, height: crownSize, color: a, flexShrink: 0 }} />
            <h1
              className="font-display uppercase"
              style={{
                fontSize: cityFont,
                lineHeight: 0.9,
                letterSpacing: -1.5,
                wordBreak: "break-word",
                maxWidth: "100%",
              }}
            >
              {w.city}
            </h1>
          </div>
          {(config.showWinnerDescription || config.showWinnerRegion) && (
            <p
              className="mt-1.5"
              style={{
                fontFamily: '"Bodoni Moda", serif',
                fontStyle: "italic",
                fontSize: 18,
                color: "rgba(0,0,0,0.6)",
                lineHeight: 1.25,
              }}
            >
              {config.showWinnerDescription && w.skatepark}
              {config.showWinnerDescription && config.showWinnerRegion && w.skatepark && w.region && " · "}
              {config.showWinnerRegion && w.region}
            </p>
          )}
        </div>

        {/* Ends-at pill — own section so its spacing matches title↔winner gap */}
        {config.showEndsAt && data.campaign?.ends_at && (
          <div className="flex justify-center">
            <EndsAtPill endsAt={data.campaign.ends_at} accent={a} />
          </div>
        )}

        {/* Stats */}
        {config.showStats && (
          <div className="grid grid-cols-2 gap-3">
            <div
              className="p-3.5 rounded-2xl"
              style={{ background: "white", boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)" }}
            >
              <p
                className="text-[9px] uppercase tracking-[0.22em]"
                style={{ color: "rgba(0,0,0,0.5)" }}
              >
                Głosów dla zwycięzcy
              </p>
              <p
                className="font-display tabular-nums"
                style={{ fontSize: 40, color: a, lineHeight: 1 }}
              >
                {w.votes.toLocaleString("pl-PL")}
              </p>
              <p className="text-[10px] mt-1 tabular-nums" style={{ color: "rgba(0,0,0,0.5)" }}>
                {w.pct}% wszystkich
              </p>
            </div>
            <div
              className="p-3.5 rounded-2xl"
              style={{ background: "white", boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)" }}
            >
              <p
                className="text-[9px] uppercase tracking-[0.22em]"
                style={{ color: "rgba(0,0,0,0.5)" }}
              >
                Wszystkich głosów
              </p>
              <p
                className="font-display tabular-nums"
                style={{ fontSize: 40, lineHeight: 1 }}
              >
                {data.total.toLocaleString("pl-PL")}
              </p>
              <p
                className="text-[10px] mt-1 uppercase tracking-[0.18em] tabular-nums"
                style={{ color: "rgba(0,0,0,0.5)" }}
              >
                {activity.days} {activity.days === 1 ? "dzień" : "dni"}
                {activity.perDay > 0 && (
                  <>
                    {" · "}
                    średnio {activity.perDay.toLocaleString("pl-PL")}/dzień
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* TOP list */}
        {config.showTop && (
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.3em] mb-2"
              style={{ color: "rgba(0,0,0,0.5)" }}
            >
              {topLabel}
            </p>
            <TopList rows={fullTop} accent={a} config={config} />
          </div>
        )}
      </div>

      <ShareFooter config={config} />
    </div>
  );
}

function PostCard({ data, config }) {
  const w = data.winner;
  const a = config.accent;
  const winnerBase = SIZE_MAP.post.winner[config.winnerSize] || 52;
  const campBase = SIZE_MAP.post.campaign[config.campaignTitleSize] || 12;
  const fullTop = [{ ...w, isWinner: true }, ...(data.top || [])].slice(0, 5);
  const topLabel = topListLabel(config, data.campaign, 5);
  const cityFontSize = winnerFontSize(w.city, winnerBase);
  const crownSize = Math.round(cityFontSize * 0.78);

  return (
    <div
      className="post-card relative overflow-hidden flex flex-col"
      style={{
        width: 540,
        height: 540,
        borderRadius: 24,
        background: "linear-gradient(180deg, #ffffff 0%, #f1f7f1 60%, #d8ebd8 100%)",
        padding: "16px 22px",
      }}
    >
      <div style={GRAIN_STYLE} />
      <div
        className="absolute font-display uppercase pointer-events-none select-none"
        style={{
          fontSize: 200,
          letterSpacing: -8,
          color: `${a}0d`,
          right: -16,
          top: -60,
          lineHeight: 0.85,
        }}
      >
        TOUR
      </div>

      <ShareHeader accent={a} />

      {/* CONTENT — centered vertically between header & footer */}
      <div className="relative flex-1 min-h-0 flex flex-col justify-center gap-3 my-2 z-10">
        {(config.showCampaignTitle || config.showCampaignDescription) && (
          <div className="text-center">
            {config.showCampaignTitle && data.campaign?.title && (
              <p
                className="font-display uppercase truncate"
                style={{
                  fontSize: campBase,
                  letterSpacing: -0.3,
                  color: "rgba(0,0,0,0.7)",
                }}
              >
                {data.campaign.title}
              </p>
            )}
            {config.showCampaignDescription && data.campaign?.description && (
              <p
                className="mt-0.5 mx-auto line-clamp-2"
                style={{
                  fontFamily: '"Bodoni Moda", serif',
                  fontStyle: "italic",
                  fontSize: Math.max(10, Math.round(campBase * 0.85)),
                  color: "rgba(0,0,0,0.55)",
                  lineHeight: 1.3,
                  maxWidth: 420,
                }}
              >
                {data.campaign.description}
              </p>
            )}
          </div>
        )}

        {/* Winner block — centered (Post: no votes count; redundant vs TOP 5) */}
        <div className="text-center">
          <p
            className="text-[10px] uppercase tracking-[0.4em]"
            style={{ color: "rgba(0,0,0,0.5)" }}
          >
            Zwycięzca
          </p>
          <div className="flex items-end justify-center gap-2 mt-1">
            <Crown style={{ width: crownSize, height: crownSize, color: a, flexShrink: 0 }} />
            <h1
              className="font-display uppercase"
              style={{
                fontSize: cityFontSize,
                lineHeight: 0.88,
                letterSpacing: -1,
                wordBreak: "break-word",
                hyphens: "auto",
                maxWidth: "100%",
              }}
            >
              {w.city}
            </h1>
          </div>
          {(config.showWinnerDescription || config.showWinnerRegion) && (
            <p
              className="mt-0.5"
              style={{
                fontFamily: '"Bodoni Moda", serif',
                fontStyle: "italic",
                fontSize: 12,
                color: "rgba(0,0,0,0.55)",
                lineHeight: 1.2,
              }}
            >
              {config.showWinnerDescription && w.skatepark}
              {config.showWinnerDescription && config.showWinnerRegion && w.skatepark && w.region && " · "}
              {config.showWinnerRegion && w.region}
            </p>
          )}
        </div>

        {/* Ends-at pill — own section so its spacing matches title↔winner gap */}
        {config.showEndsAt && data.campaign?.ends_at && (
          <div className="flex justify-center">
            <EndsAtPill endsAt={data.campaign.ends_at} accent={a} />
          </div>
        )}

        {/* TOP list */}
        {config.showTop && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p
                className="text-[10px] uppercase tracking-[0.3em] truncate"
                style={{ color: "rgba(0,0,0,0.5)" }}
              >
                {topLabel}
              </p>
              <p
                className="text-[10px] tabular-nums"
                style={{ color: "rgba(0,0,0,0.4)" }}
              >
                {data.total.toLocaleString("pl-PL")} głosów
              </p>
            </div>
            <TopList rows={fullTop} accent={a} config={config} compact />
          </div>
        )}
      </div>

      <ShareFooter config={config} compact />
    </div>
  );
}

export default function ShareCardPreview() {
  const [data, setData] = useState(FALLBACK);
  const [config, setConfig] = useState(SC_DEFAULT_CONFIG);

  useEffect(() => {
    document.documentElement.classList.add("allow-scroll");
    api
      .get("/campaign/active")
      .then(({ data: d }) => {
        if (!d.campaign) return;
        let resolved = { ...SC_DEFAULT_CONFIG };
        if (d.campaign.share_card_config) {
          resolved = { ...resolved, ...d.campaign.share_card_config };
        }
        try {
          const raw = localStorage.getItem(`scootive_sharecard_${d.campaign.id}`);
          if (raw) resolved = { ...resolved, ...JSON.parse(raw) };
        } catch {
          /* ignore */
        }
        setConfig(resolved);

        const board = d.leaderboard || [];
        if (board.length === 0) return;
        const w = board[0];
        setData({
          campaign: d.campaign,
          total: d.total_votes || 0,
          winner: {
            city: w.city,
            skatepark: w.skatepark || "",
            region: w.region || "",
            votes: w.votes,
            pct: w.percentage,
          },
          top: board.slice(1, 5).map((r) => ({
            city: r.city,
            skatepark: r.skatepark || "",
            region: r.region || "",
            votes: r.votes,
            pct: r.percentage,
          })),
        });
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#f4f5f3] py-12 px-5">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-display text-4xl uppercase tracking-tight mb-2">
          Share Card · Concept Preview
        </h1>
        <p className="text-sm text-black/55 mb-8 max-w-2xl">
          Tu pokazane w 50% skali. Pełna konfiguracja dostępna w panelu admina &rarr;{" "}
          <a href="/admin" className="underline brand-green">
            Share Card tab
          </a>
          .
        </p>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-black/55">
                IG Story · TikTok
              </span>
              <span className="text-[10px] tabular-nums bg-black/5 px-2 py-1 rounded-full text-black/55">
                1080 × 1920
              </span>
            </div>
            <StoryCard data={data} config={config} />
          </div>

          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-black/55">
                IG Post · FB Post
              </span>
              <span className="text-[10px] tabular-nums bg-black/5 px-2 py-1 rounded-full text-black/55">
                1080 × 1080
              </span>
            </div>
            <PostCard data={data} config={config} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { StoryCard, PostCard };
